import { useEffect, useMemo, useState } from "react";
import { Check, X } from "lucide-react";
import { toast } from "react-toastify";
import {
  getActiveCampaignsForInvite,
  inviteInfluencerToCampaign,
  listPipelineItemsForCampaign,
} from "../../services/marketplaceApi";
import {
  formatCompactNumber,
  formatCurrency,
  getCampaignBudgetLabel,
  normalizeInfluencerPackages,
} from "./inviteInfluencerUtils";
import { useNavigate } from "react-router-dom";
import { FaArrowRight } from "react-icons/fa";

const steps = ["Campaign", "Confirm", "Done"];

function isInfluencerAlreadyShortlistedForCampaign(items, influencer) {
  const cleanId = String(influencer?.id || "").trim();
  const cleanUser = String(influencer?.username || influencer?.handle || "")
    .trim()
    .toLowerCase();
  if (!cleanId && !cleanUser) return false;

  return items.some((item) => {
    const itemInfluencerId = String(item?.influencerId || "").trim();
    const itemUsername = String(item?.username || "").trim().toLowerCase();
    if (cleanId && itemInfluencerId === cleanId) return true;
    if (cleanUser && itemUsername === cleanUser) return true;
    if (cleanUser && itemInfluencerId.toLowerCase() === cleanUser) return true;
    return false;
  });
}

const StepIndicator = ({ step }) => (
  <div className="flex items-center gap-2 mb-6">
    {steps.map((label, index) => {
      const position = index + 1;
      const isDone = position < step;
      const isActive = position === step;
      return (
        <div key={label} className="flex items-center flex-1 last:flex-[0.8]">
          <div className="flex flex-col items-center">
            <span
              className={`h-8 w-8 rounded-full border flex items-center justify-center text-xs font-semibold ${isDone
                  ? "bg-emerald-500 border-emerald-500 text-white"
                  : isActive
                    ? "bg-orange-500 border-orange-500 text-white"
                    : "bg-white border-gray-300 text-gray-500"
                }`}
            >
              {isDone ? <Check size={16} /> : position}
            </span>
            <span className="text-xs text-gray-500 mt-1">{label}</span>
          </div>
          {index < steps.length - 1 && (
            <div
              className={`h-[2px] flex-1 mx-2 ${position < step ? "bg-emerald-500" : "bg-gray-200"
                }`}
            />
          )}
        </div>
      );
    })}
  </div>
);

const InviteInfluencerWizard = ({ isOpen, onClose, influencer }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [campaignError, setCampaignError] = useState("");
  const [selectedCampaignId, setSelectedCampaignId] = useState("");
  const [selectedPackageId, setSelectedPackageId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const packageOptions = useMemo(
    () => normalizeInfluencerPackages(influencer?.packages),
    [influencer?.packages]
  );

  const selectedCampaign = useMemo(
    () => campaigns.find((item) => item.id === selectedCampaignId) || null,
    [campaigns, selectedCampaignId]
  );

  const selectedPackage = useMemo(
    () => packageOptions.find((item) => item.id === selectedPackageId) || null,
    [packageOptions, selectedPackageId]
  );

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoadingCampaigns(true);
    setCampaignError("");
    setSubmitError("");
    setStep(1);
    setSelectedCampaignId("");
    setSelectedPackageId(packageOptions[0]?.id || "");

    getActiveCampaignsForInvite()
      .then((data) => {
        if (cancelled) return;
        setCampaigns(data);
        if (data.length === 1) {
          setSelectedCampaignId(data[0].id);
        }
      })
      .catch((error) => {
        if (cancelled) return;
        setCampaignError(error?.message || "Failed to load campaigns");
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingCampaigns(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, packageOptions]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !submitting) {
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose, submitting]);

  if (!isOpen) return null;

  const canContinue = Boolean(selectedCampaignId) && Boolean(selectedPackageId);

  const handleContinue = () => {
    if (!canContinue) return;
    setSubmitError("");
    setStep(2);
  };

  const handleConfirm = async () => {
    if (!selectedCampaign || !influencer?.id) {
      setSubmitError("Influencer or campaign details are missing.");
      return;
    }
    setSubmitError("");
    setSubmitting(true);
    try {
      const pipelineItems = await listPipelineItemsForCampaign(selectedCampaign.id);
      if (isInfluencerAlreadyShortlistedForCampaign(pipelineItems, influencer)) {
        toast.warning(
          "You have already shortlisted this influencer for this campaign."
        );
        return;
      }
      await inviteInfluencerToCampaign({
        influencerId: influencer.id,
        campaignId: selectedCampaign.id,
        influencer,
        selectedPackage,
      });
      setStep(3);
    } catch (error) {
      setSubmitError(error?.message || "Failed to add influencer to pipeline");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-x-0 bottom-0 top-[70px] z-[60] bg-black/40 backdrop-blur-[1px] flex items-center justify-center p-2 sm:p-4">
      <div className="w-full max-w-xl max-h-[80vh] overflow-y-auto rounded-xl bg-white shadow-xl border border-gray-200 p-4">
        <div className="flex items-start justify-between gap-2">
          <h2 className="text-lg lg:text-xl font-semibold text-gray-900 tracking-tight">
            {step === 1 && "Select campaign"}
            {step === 2 && "Review & confirm"}
            {step === 3 && "Added to pipeline!"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-xl border border-gray-200 bg-gray-50 flex items-center justify-center text-gray-500 hover:text-gray-800 hover:bg-gray-100"
            aria-label="Close invite wizard"
            disabled={submitting}
          >
            <X size={18} />
          </button>
        </div>

        <StepIndicator step={step} />

        {step === 1 && (
          <>
            <p className="text-xs lg:text-lg text-gray-700 mb-4">
              Add {influencer?.name || "this influencer"} to which campaign?
            </p>

            {loadingCampaigns && (
              <div className="rounded-lg border border-gray-200 p-4 text-gray-500">
                Loading active campaigns...
              </div>
            )}

            {!loadingCampaigns && campaignError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
                {campaignError}
              </div>
            )}

            {!loadingCampaigns && !campaignError && campaigns.length === 0 && (
              <div className="rounded-lg border border-gray-200 p-4 text-gray-600">
                No active campaign found. Create or activate a campaign first.
              </div>
            )}

            {!loadingCampaigns && !campaignError && campaigns.length > 0 && (
              <div className="space-y-3 max-h-[280px] overflow-auto pr-1">
                {campaigns.map((campaign) => {
                  const selected = selectedCampaignId === campaign.id;
                  return (
                    <button
                      key={campaign.id}
                      type="button"
                      onClick={() => setSelectedCampaignId(campaign.id)}
                      className={`w-full rounded-lg border p-2 text-xs lg:text-base text-left transition ${selected
                          ? "border-orange-400 bg-orange-50 text-orange-700"
                          : "border-gray-200 hover:border-orange-200"
                        }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <p className="text-base lg:text-lg font-semibold text-gray-900">{campaign.name}</p>
                          <p className="text-gray-500">
                            {campaign.totalAffiliates || 0} influencers · {getCampaignBudgetLabel(campaign)}
                          </p>
                        </div>
                        <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                          Active
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            <div className="mt-5">
              <p className="text-xs lg:text-lg font-medium text-gray-800 mb-2">Package</p>
              <div className="flex flex-wrap gap-2">
                {packageOptions.map((pkg) => {
                  const selected = selectedPackageId === pkg.id;
                  return (
                    <button
                      key={pkg.id}
                      type="button"
                      onClick={() => setSelectedPackageId(pkg.id)}
                      className={`rounded-lg border px-4 py-2 text-xs font-medium ${selected
                          ? "border-orange-500 text-orange-700 bg-orange-50"
                          : "border-gray-200 text-gray-700 hover:border-orange-200"
                        }`}
                    >
                      {pkg.title}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-8 flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl border border-gray-200 text-xs lg:text-base px-4 py-2 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleContinue}
                disabled={!canContinue}
                className="rounded-lg bg-orange-500 text-xs lg:text-base px-4 py-2 text-white font-semibold hover:bg-orange-600 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Continue

              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="space-y-4">
              <div className="rounded-lg bg-gray-50 p-4 border border-gray-100">
                <p className="text-base lg:text-lg font-semibold text-gray-900">{influencer?.name}</p>
                <p className="text-xs lg:text-lg text-gray-500">
                  @{influencer?.username} · {formatCompactNumber(influencer?.followers)} followers
                </p>
              </div>

              <div className="grid grid-cols-1 grid-cols-2 gap-2 lg:gap-3">
                <div className="rounded-lg bg-gray-50 p-2 border border-gray-100">
                  <p className="text-gray-500 mb-1">Campaign</p>
                  <p className="text-xs lg:text-lg text-gray-900 tracking-tight">
                    {selectedCampaign?.name || "-"}
                  </p>
                </div>
                <div className="rounded-lg bg-gray-50 p-2 border border-gray-100">
                  <p className="text-gray-500 mb-1">Package</p>
                  <p className="text-xs lg:text-lg text-gray-900 tracking-tight">
                    {selectedPackage?.title || "-"}
                  </p>
                </div>
                <div className="rounded-lg bg-gray-50 p-2 border border-gray-100">
                  <p className="text-gray-500 mb-1">Stage</p>
                  <p className="text-xs lg:text-lg text-orange-600 tracking-tight">Shortlisted</p>
                </div>
                <div className="rounded-lg bg-gray-50 p-2 border border-gray-100">
                  <p className="text-gray-500 mb-1">Rate</p>
                  <p className="text-xs lg:text-lg text-gray-900 tracking-tight">
                    {selectedPackage?.price == null
                      ? "To be discussed"
                      : formatCurrency(selectedPackage.price, selectedPackage.currencySymbol)}
                  </p>
                </div>
              </div>
            </div>

            {submitError && (
              <div className="mt-4 text-xs lg:text-base rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">
                {submitError}
              </div>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setStep(1)}
                disabled={submitting}
                className="rounded-lg border border-gray-200 text-xs lg:text-base px-6 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={submitting}
                className="rounded-lg bg-orange-500 text-xs lg:text-base px-6 py-2 text-white hover:bg-orange-600 disabled:opacity-60"
              >
                {submitting ? "Adding..." : "Add to pipeline"}
              </button>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div className="text-xs lg:text-base rounded-lg border border-emerald-100 bg-emerald-50 p-4 sm:p-8 text-center">
              <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600">
                <Check size={30} />
              </div>
              <p className="text-sm sm:text-xl font-semibold text-gray-900 mb-2">Added to pipeline!</p>
              <p className="lg:text-lg text-base text-gray-600">
                {influencer?.name} invited to {selectedCampaign?.name} at <strong>Shortlisted</strong> stage
              </p>
            </div>

            <div className="mt-5 space-y-2">
              {[
                `Shortlisted - ${influencer?.name} has been added to your pipeline`,
                "Contacted - Send DM / Email / WhatsApp",
                "Negotiating - Align deliverables & rate",
                "Confirmed - Sign contract & lock deal",
                "Live / Done - Content goes live",
              ].map((label, index) => (
                <div
                  key={label}
                  className={`text-xs lg:text-base rounded-lg border px-4 py-3 font-medium ${index === 0
                      ? "border-orange-300 bg-orange-50 text-orange-700"
                      : "border-gray-200 text-gray-600"
                    }`}
                >
                  {label}
                </div>
              ))}
            </div>

            {/* <div className="mt-8 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl bg-orange-500 px-6 py-2 text-white font-semibold hover:bg-orange-600"
              >
                Close
              </button>
            </div> */}
            <div className="mt-8 flex justify-end gap-2 md:gap-3">
              {/* Continue Discovery */}
              <button
                type="button"
                onClick={onClose}
                className="text-xs lg:text-base rounded-xl border border-gray-200 px-2 md:px-6 py-2 text-gray-700 font-medium hover:bg-gray-50 gap-2"
              >
                Continue Discovery
              </button>

              {/* View Pipeline */}
              <button
                type="button"
                onClick={() => {
                  onClose();
                  navigate("/company/dashboard/pipelines");
                }}
                className="text-xs flex items-center justify-center lg:text-base rounded-xl bg-orange-500 px-2 md:px-6 py-2 text-white font-medium hover:bg-orange-600 gap-2"
              >
                View Pipeline <FaArrowRight />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InviteInfluencerWizard;
